import React, { useState } from 'react';
import { aiApi, type NudgeAgentResult, type MeetingBriefResult, type PlaybookSuggestionResult } from '../../api/client';
import { useApp } from '../../context/AppContext';
import type { LeadData } from '../../api/client';

type AiTab = 'nudge' | 'brief' | 'playbook' | 'score' | 'coach';

interface Props {
  lead: LeadData;
  onClose: () => void;
}

export default function AiPanel({ lead, onClose }: Props) {
  const { toast } = useApp();
  const [tab, setTab] = useState<AiTab>('nudge');
  const [loading, setLoading] = useState(false);
  const [coachMsg, setCoachMsg] = useState('');
  const [coachReply, setCoachReply] = useState('');

  // Result states
  const [nudgeResult, setNudgeResult] = useState<NudgeAgentResult | null>(null);
  const [briefResult, setBriefResult] = useState<MeetingBriefResult | null>(null);
  const [playbookResult, setPlaybookResult] = useState<PlaybookSuggestionResult | null>(null);
  const [scoreResult, setScoreResult] = useState<{ score: number; heat: string; reasoning: string; topSignals: string[] } | null>(null);

  async function runAgent() {
    setLoading(true);
    try {
      if (tab === 'nudge') {
        const r = await aiApi.generateNudge(lead._id);
        setNudgeResult(r);
      } else if (tab === 'brief') {
        const r = await aiApi.meetingBrief(lead._id);
        setBriefResult(r);
      } else if (tab === 'playbook') {
        const r = await aiApi.suggestPlaybook(lead._id);
        setPlaybookResult(r);
      } else if (tab === 'score') {
        const r = await aiApi.scoreLead(lead._id);
        setScoreResult(r);
      }
      toast('AI analysis complete');
    } catch (e) {
      toast((e as Error).message || 'AI request failed', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function sendCoach() {
    if (!coachMsg.trim()) return;
    setLoading(true);
    try {
      const r = await aiApi.coach(coachMsg, { leadName: lead.name, org: lead.org, intent: lead.intent, heat: lead.heat });
      setCoachReply(r.reply);
      setCoachMsg('');
    } catch (e) {
      toast((e as Error).message || 'Coach unavailable', 'error');
    } finally {
      setLoading(false);
    }
  }

  const tabs: { id: AiTab; icon: string; label: string }[] = [
    { id: 'nudge',    icon: 'ti-send',         label: 'Nudge'     },
    { id: 'brief',    icon: 'ti-brain',         label: 'Brief'     },
    { id: 'playbook', icon: 'ti-book-2',        label: 'Playbook'  },
    { id: 'score',    icon: 'ti-chart-bar',     label: 'Score'     },
    { id: 'coach',    icon: 'ti-message-circle', label: 'Coach'    },
  ];

  const HEAT_COLOR: Record<string, string> = { hot: '#EF4444', warm: '#C8973A', cold: '#718096' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,.4)' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', borderRadius: '16px 16px 0 0' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(200,151,58,.2)', border: '1px solid rgba(200,151,58,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-sparkles" style={{ color: '#C8973A', fontSize: '18px' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'white' }}>AI Sales Agent — {lead.name}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,.5)' }}>{lead.org} · {lead.intent}% intent · Gemini 2.0 Flash</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: '20px', padding: '2px' }}>×</button>
        </div>

        {/* Agent tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', flexShrink: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: '10px 4px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: tab === t.id ? '#0F1B2D' : '#718096', borderBottom: tab === t.id ? '2px solid #C8973A' : '2px solid transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
              <i className={`ti ${t.icon}`} style={{ fontSize: '16px' }} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* ── NUDGE TAB ── */}
          {tab === 'nudge' && (
            <div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F1B2D', marginBottom: '4px' }}>Smart Nudge Generator</div>
                <div style={{ fontSize: '12px', color: '#718096' }}>AI crafts a personalised follow-up based on {lead.name}'s engagement signals.</div>
              </div>
              {nudgeResult ? (
                <div>
                  <div style={{ background: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                    {nudgeResult.subject && <div style={{ fontSize: '11px', fontWeight: '700', color: '#276749', marginBottom: '6px', textTransform: 'uppercase' }}>Subject: {nudgeResult.subject}</div>}
                    <div style={{ fontSize: '14px', color: '#1A202C', lineHeight: 1.6 }}>{nudgeResult.message}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: '#718096' }}>
                    <span style={{ padding: '3px 9px', borderRadius: '20px', background: '#EDF2F7', fontWeight: '600' }}>Tone: {nudgeResult.tone}</span>
                    <span style={{ padding: '3px 9px', borderRadius: '20px', background: '#EDF2F7', fontWeight: '600' }}>Follow up in: {nudgeResult.followUpIn}</span>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '11px', color: '#718096', background: '#F8FAFC', padding: '10px', borderRadius: '8px' }}>
                    <strong>AI reasoning:</strong> {nudgeResult.reasoning}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                    <button onClick={() => { navigator.clipboard.writeText(nudgeResult.message); toast('Copied to clipboard!'); }} style={{ flex: 1, padding: '9px', borderRadius: '8px', background: '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                      <i className="ti ti-copy" /> Copy message
                    </button>
                    <button onClick={() => setNudgeResult(null)} style={{ padding: '9px 14px', borderRadius: '8px', border: '1.5px solid #E2E8F0', background: 'white', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', color: '#4A5568' }}>
                      Regenerate
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={runAgent} disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: '10px', background: loading ? '#CBD5E0' : 'linear-gradient(135deg, #C8973A, #E8B55A)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {loading ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Generating…</> : <><i className="ti ti-sparkles" /> Generate AI Nudge</>}
                </button>
              )}
            </div>
          )}

          {/* ── BRIEF TAB ── */}
          {tab === 'brief' && (
            <div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F1B2D', marginBottom: '4px' }}>Pre-Meeting Brief</div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Get a complete AI briefing before your call with {lead.name}.</div>
              </div>
              {briefResult ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ padding: '12px 14px', background: '#FDF4E7', border: '1px solid #E8D5A3', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#7A5A1A', marginBottom: '5px', textTransform: 'uppercase' }}>Opening line</div>
                    <div style={{ fontSize: '13px', color: '#1A202C', fontStyle: 'italic' }}>"{briefResult.openingLine}"</div>
                  </div>
                  {[
                    { title: '🔍 Key insights', items: briefResult.keyInsights },
                    { title: '❓ Discovery questions', items: briefResult.suggestedQuestions },
                    { title: '💬 Talking points', items: briefResult.talkingPoints },
                    { title: '🛡️ Objection handling', items: briefResult.objectionHandling },
                    { title: '✅ Next steps', items: briefResult.nextSteps },
                  ].map(section => (
                    <div key={section.title} style={{ padding: '12px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: '#0F1B2D', marginBottom: '8px' }}>{section.title}</div>
                      {section.items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#2D3748', marginBottom: '5px' }}>
                          <span style={{ color: '#C8973A', flexShrink: 0 }}>›</span> {item}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <button onClick={runAgent} disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: '10px', background: loading ? '#CBD5E0' : '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {loading ? <><i className="ti ti-loader-2" /> Generating…</> : <><i className="ti ti-brain" /> Generate Meeting Brief</>}
                </button>
              )}
            </div>
          )}

          {/* ── PLAYBOOK TAB ── */}
          {tab === 'playbook' && (
            <div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F1B2D', marginBottom: '4px' }}>Playbook Recommendation</div>
                <div style={{ fontSize: '12px', color: '#718096' }}>AI recommends which collaterals to include and writes the landing page copy.</div>
              </div>
              {playbookResult ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ padding: '14px', background: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#276749', marginBottom: '5px' }}>HERO HEADLINE</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F1B2D' }}>{playbookResult.heroHeadline}</div>
                  </div>
                  <div style={{ padding: '14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', marginBottom: '5px' }}>PERSONAL MESSAGE</div>
                    <div style={{ fontSize: '13px', color: '#2D3748', fontStyle: 'italic' }}>{playbookResult.personalMessage}</div>
                  </div>
                  <div style={{ padding: '14px', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#4338CA', marginBottom: '8px' }}>RECOMMENDED COLLATERALS (indices)</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {playbookResult.recommendedCollaterals.map(idx => (
                        <span key={idx} style={{ padding: '3px 10px', borderRadius: '20px', background: '#6366F1', color: 'white', fontSize: '12px', fontWeight: '700' }}>#{idx}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#718096', padding: '10px', background: '#F8FAFC', borderRadius: '8px' }}>
                    <strong>Reasoning:</strong> {playbookResult.reasoning}
                  </div>
                </div>
              ) : (
                <button onClick={runAgent} disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: '10px', background: loading ? '#CBD5E0' : '#6366F1', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {loading ? <><i className="ti ti-loader-2" /> Analysing…</> : <><i className="ti ti-book-2" /> Suggest Playbook</>}
                </button>
              )}
            </div>
          )}

          {/* ── SCORE TAB ── */}
          {tab === 'score' && (
            <div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F1B2D', marginBottom: '4px' }}>AI Intent Rescoring</div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Re-evaluate {lead.name}'s intent score from their latest engagement signals.</div>
              </div>
              {scoreResult ? (
                <div>
                  <div style={{ textAlign: 'center', padding: '24px', background: '#0F1B2D', borderRadius: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '60px', fontWeight: '900', color: '#C8973A', lineHeight: 1 }}>{scoreResult.score}</div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,.6)', marginTop: '4px' }}>Intent Score</div>
                    <div style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 14px', borderRadius: '20px', background: HEAT_COLOR[scoreResult.heat] + '30', color: HEAT_COLOR[scoreResult.heat], fontSize: '12px', fontWeight: '700', border: `1px solid ${HEAT_COLOR[scoreResult.heat]}60` }}>
                      {scoreResult.heat.toUpperCase()}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#4A5568', background: '#F8FAFC', padding: '12px', borderRadius: '8px', marginBottom: '10px' }}>{scoreResult.reasoning}</div>
                  {scoreResult.topSignals?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '6px' }}>Top signals</div>
                      {scoreResult.topSignals.map((s, i) => <div key={i} style={{ fontSize: '12px', color: '#2D3748', marginBottom: '4px', display: 'flex', gap: '6px' }}><span style={{ color: '#22C55E' }}>✓</span>{s}</div>)}
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={runAgent} disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: '10px', background: loading ? '#CBD5E0' : '#22C55E', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {loading ? <><i className="ti ti-loader-2" /> Scoring…</> : <><i className="ti ti-chart-bar" /> Rescore with AI</>}
                </button>
              )}
            </div>
          )}

          {/* ── COACH TAB ── */}
          {tab === 'coach' && (
            <div>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F1B2D', marginBottom: '4px' }}>AI Sales Coach</div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Ask anything about this deal — strategy, objections, pricing, next steps.</div>
              </div>
              {coachReply && (
                <div style={{ background: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#276749', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="ti ti-sparkles" /> Gemini Coach
                  </div>
                  <div style={{ fontSize: '13px', color: '#1A202C', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{coachReply}</div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={coachMsg}
                  onChange={e => setCoachMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendCoach()}
                  placeholder={`Ask about ${lead.name}'s deal…`}
                  style={{ flex: 1, padding: '10px 13px', border: '1.5px solid #E2E8F0', borderRadius: '9px', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }}
                />
                <button onClick={sendCoach} disabled={loading || !coachMsg.trim()} style={{ padding: '10px 16px', borderRadius: '9px', background: loading || !coachMsg.trim() ? '#CBD5E0' : '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: loading || !coachMsg.trim() ? 'default' : 'pointer' }}>
                  {loading ? '…' : 'Ask'}
                </button>
              </div>
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {[
                  `How do I close ${lead.name}?`,
                  'What objection should I prepare for?',
                  'Best time to follow up?',
                  'Suggest a negotiation angle',
                ].map(q => (
                  <button key={q} onClick={() => setCoachMsg(q)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px', border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', color: '#4A5568' }}>{q}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
